import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { 
  ArrowLeft, BarChart3, Eye, Clock, CheckCircle, 
  Link2, Users, TrendingUp, ChevronDown, ChevronRight
} from "lucide-react";

const PHASE_LABELS = {
  validated: "Link Opened",
  intro: "Introduction",
  v1_explain: "V1 Overview",
  v1_running: "V1 Live Demo",
  v1_postrace: "Post-Race",
  v2_transition: "V2 Intro",
  v2_prompt: "Mode Select",
  v2_running: "V2 Broadcast",
  v2_summary: "V2 Summary",
  v3_transition: "V3 Intro",
  v3_running: "V3 Cycling",
  v4_expansion: "Platform Expansion",
  close: "Close"
};

const DemoAnalyticsPage = ({ token }) => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedLink, setExpandedLink] = useState(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await axios.get(`${API}/demo-links/analytics`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAnalytics(res.data);
      } catch (e) {
        console.error("Failed to load analytics", e);
      }
      setLoading(false);
    };
    fetchAnalytics();
    const interval = setInterval(fetchAnalytics, 15000);
    return () => clearInterval(interval);
  }, [token]);

  const formatTime = (seconds) => {
    if (!seconds) return "0s";
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  const getProgressColour = (pct) => {
    if (pct >= 80) return "#10B981";
    if (pct >= 50) return "#F59E0B";
    if (pct >= 20) return "#F97316";
    return "#EF4444";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#090a0d] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#F97316] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const summary = analytics?.summary || {};
  const links = analytics?.links || [];

  return (
    <div className="min-h-screen bg-[#090a0d]" data-testid="demo-analytics-page">
      {/* Header */}
      <header className="bg-[#0d0f12]/95 backdrop-blur-sm border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-5 h-5 text-[#F97316]" />
            <div>
              <div className="text-[#F97316] text-xs tracking-widest font-bold uppercase">SALES INTELLIGENCE</div>
              <h1 className="font-heading text-xl font-bold text-white">Demo Link Analytics</h1>
            </div>
          </div>
          <Button
            onClick={() => navigate("/")}
            variant="ghost"
            className="text-white/50 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="analytics-summary">
          <SummaryCard icon={Link2} label="Total Links" value={summary.total_links || 0} colour="#F97316" />
          <SummaryCard icon={Link2} label="Active Links" value={summary.active_links || 0} colour="#10B981" />
          <SummaryCard icon={Eye} label="Total Views" value={summary.total_views || 0} colour="#3B82F6" />
          <SummaryCard icon={Users} label="Sessions" value={summary.total_sessions || 0} colour="#A855F7" />
          <SummaryCard icon={CheckCircle} label="Completed" value={summary.total_completed || 0} colour="#10B981" />
          <SummaryCard icon={TrendingUp} label="Completion Rate" value={`${summary.completion_rate || 0}%`} colour="#F59E0B" />
        </div>

        {/* Links List */}
        {links.length === 0 ? (
          <div className="text-center py-16">
            <Link2 className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <h2 className="text-white/50 font-heading text-lg mb-2">No demo links yet</h2>
            <p className="text-white/30 text-sm">Create a shareable demo link from the Dashboard to start tracking.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {links.map((link) => (
              <LinkAnalyticsCard
                key={link.link_id}
                link={link}
                isExpanded={expandedLink === link.link_id}
                onToggle={() => setExpandedLink(expandedLink === link.link_id ? null : link.link_id)}
                formatTime={formatTime}
                formatDate={formatDate}
                getProgressColour={getProgressColour}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

// =============================================================================
// SUMMARY CARD
// =============================================================================

const SummaryCard = ({ icon: Icon, label, value, colour }) => (
  <div className="p-4 rounded-xl border border-white/10 bg-white/[0.03]" data-testid={`summary-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4" style={{ color: colour }} />
      <span className="text-white/40 text-xs uppercase">{label}</span>
    </div>
    <div className="text-2xl font-bold text-white">{value}</div>
  </div>
);

// =============================================================================
// LINK ANALYTICS CARD
// =============================================================================

const LinkAnalyticsCard = ({ link, isExpanded, onToggle, formatTime, formatDate, getProgressColour }) => {
  const funnelEntries = Object.entries(link.completion_funnel || {});
  const maxFunnelValue = Math.max(...funnelEntries.map(([, v]) => v), 1);

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden" data-testid={`analytics-link-${link.link_id}`}>
      {/* Link Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-white/[0.02] transition-colors text-left"
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${link.is_active ? 'bg-[#10B981]' : 'bg-white/20'}`} />
          <div className="min-w-0">
            <div className="text-white font-semibold truncate">{link.label}</div>
            <div className="text-white/30 text-xs">Created {formatDate(link.created_at)}</div>
          </div>
        </div>

        <div className="flex items-center gap-6 flex-shrink-0">
          <div className="text-center">
            <div className="text-white text-sm font-bold">{link.views_used}/{link.max_views}</div>
            <div className="text-white/30 text-[10px] uppercase">Views</div>
          </div>
          <div className="text-center">
            <div className="text-white text-sm font-bold">{link.total_sessions}</div>
            <div className="text-white/30 text-[10px] uppercase">Sessions</div>
          </div>
          <div className="text-center">
            <div className="text-sm font-bold" style={{ color: getProgressColour(link.avg_progress_pct) }}>
              {link.avg_progress_pct}%
            </div>
            <div className="text-white/30 text-[10px] uppercase">Avg Progress</div>
          </div>
          <div className="text-center">
            <div className="text-white text-sm font-bold">{formatTime(link.avg_time_seconds)}</div>
            <div className="text-white/30 text-[10px] uppercase">Avg Time</div>
          </div>
          <div className="text-center">
            <div className="text-[#10B981] text-sm font-bold">{link.completed_sessions}</div>
            <div className="text-white/30 text-[10px] uppercase">Completed</div>
          </div>
          {isExpanded ? <ChevronDown className="w-4 h-4 text-white/30" /> : <ChevronRight className="w-4 h-4 text-white/30" />}
        </div>
      </button>

      {/* Expanded Detail */}
      {isExpanded && (
        <div className="border-t border-white/10 p-4 space-y-5">
          {/* Completion Funnel */}
          <div>
            <h4 className="text-white/60 text-xs uppercase tracking-wider mb-3">Drop-off Funnel</h4>
            <div className="space-y-1.5">
              {funnelEntries.map(([phaseName, count]) => (
                <div key={phaseName} className="flex items-center gap-3">
                  <div className="w-28 text-white/40 text-xs truncate">{PHASE_LABELS[phaseName] || phaseName}</div>
                  <div className="flex-1 h-5 bg-white/5 rounded-full overflow-hidden relative">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${(count / maxFunnelValue) * 100}%`,
                        backgroundColor: count === maxFunnelValue ? '#F97316' : count > 0 ? '#F97316' : '#333',
                        opacity: count / maxFunnelValue * 0.6 + 0.4
                      }}
                    />
                    <span className="absolute inset-y-0 right-2 flex items-center text-white/60 text-[10px] font-bold">
                      {count}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Individual Sessions */}
          {link.sessions && link.sessions.length > 0 && (
            <div>
              <h4 className="text-white/60 text-xs uppercase tracking-wider mb-3">Session Detail</h4>
              <div className="space-y-2">
                {link.sessions.map((session) => {
                  const progressPct = Math.round((session.max_phase_step / 12) * 100);
                  return (
                    <div key={session.session_id} className="flex items-center gap-4 p-3 rounded-lg bg-white/[0.02] border border-white/5">
                      <div className="flex-shrink-0">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                          session.completed ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-white/10 text-white/50'
                        }`}>
                          #{session.view_number}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-white text-sm">{formatDate(session.started_at)}</span>
                          {session.completed && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-[#10B981]/20 text-[#10B981]">
                              Completed
                            </span>
                          )}
                        </div>
                        {/* Progress bar */}
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${progressPct}%`,
                                backgroundColor: getProgressColour(progressPct)
                              }}
                            />
                          </div>
                          <span className="text-white/40 text-[10px] w-8">{progressPct}%</span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-white/60 text-xs">{formatTime(session.time_spent_seconds)}</div>
                        <div className="text-white/30 text-[10px]">
                          Step {session.max_phase_step}/12
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DemoAnalyticsPage;
